import React, { useState, useEffect, useRef } from 'react';
import { FolderOpen, Search, Upload, Trash2, FileText, MessageSquare, Send, ChevronDown, ChevronUp, CheckCircle2, Circle, Clock } from 'lucide-react';
import { projectsAPI, messagesAPI } from '../../services/api';
import '../admin/AdminSections.css';

const STAGES = [
  'Στάδιο Παραγγελίας',
  'Στάδιο Βαφείου',
  'Στάδιο Παραλαβής Υλικών',
  'Στάδιο Εκφόρτωσης',
  'Στάδιο Μέτρα Κοπής',
  'Στάδιο Παραγωγής',
  'Στάδιο Συσκευασίας',
  'Στάδιο Εγκατάστασης',
  'Ολοκληρωμένο'
];

const MyProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCode, setSearchCode] = useState('');
  const [expandedProject, setExpandedProject] = useState(null);
  const [projectFiles, setProjectFiles] = useState({});
  const [projectProgress, setProjectProgress] = useState({});
  const [projectMessages, setProjectMessages] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('details');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProject = async (project) => {
    if (expandedProject === project.id) {
      setExpandedProject(null);
      return;
    }
    setExpandedProject(project.id);
    setActiveSubTab('details');
    loadProjectData(project.id);
  };

  const loadProjectData = async (projectId) => {
    try {
      const [filesRes, progressRes, msgsRes] = await Promise.all([
        projectsAPI.getFiles(projectId),
        projectsAPI.getProgress(projectId),
        messagesAPI.getByProject(projectId)
      ]);
      setProjectFiles(prev => ({ ...prev, [projectId]: filesRes.data }));
      setProjectProgress(prev => ({ ...prev, [projectId]: progressRes.data }));
      setProjectMessages(prev => ({ ...prev, [projectId]: msgsRes.data }));
    } catch (error) {
      console.error('Error loading project data:', error);
    }
  };

  const handleFileUpload = async (projectId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      await projectsAPI.uploadFile(projectId, file);
      const filesRes = await projectsAPI.getFiles(projectId);
      setProjectFiles(prev => ({ ...prev, [projectId]: filesRes.data }));
    } catch (error) {
      alert('Σφάλμα κατά το ανέβασμα αρχείου');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (projectId, fileId) => {
    if (!window.confirm('Διαγραφή αρχείου;')) return;
    try {
      await projectsAPI.deleteFile(projectId, fileId);
      const filesRes = await projectsAPI.getFiles(projectId);
      setProjectFiles(prev => ({ ...prev, [projectId]: filesRes.data }));
    } catch (error) {
      alert('Σφάλμα κατά τη διαγραφή');
    }
  };

  const handleSendMessage = async (projectId) => {
    if (!newMessage.trim()) return;
    try {
      await messagesAPI.send({ project_id: projectId, content: newMessage });
      setNewMessage('');
      const msgsRes = await messagesAPI.getByProject(projectId);
      setProjectMessages(prev => ({ ...prev, [projectId]: msgsRes.data }));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      alert('Σφάλμα αποστολής μηνύματος');
    }
  };

  const getStageIndex = (statusValue) => {
    return STAGES.indexOf(statusValue);
  };

  const isStageCompleted = (projectId, stageName) => {
    const progress = projectProgress[projectId] || [];
    const entry = progress.find(p => p.status === stageName);
    return entry?.completed || false;
  };

  const getCurrentStageIndex = (projectStatus) => {
    return STAGES.indexOf(projectStatus);
  };

  const filteredProjects = searchCode
    ? projects.filter(p => p.project_code.toLowerCase().includes(searchCode.toLowerCase()))
    : projects;

  const getStatusColor = (status) => {
    const idx = STAGES.indexOf(status);
    if (idx === -1) return '#666';
    if (idx === STAGES.length - 1) return '#4CAF50';
    if (idx >= 5) return '#FF9800';
    return '#2196F3';
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Φόρτωση...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-section" data-testid="my-projects-section">
      <div className="section-header">
        <h2>
          <FolderOpen size={24} />
          Τα Έργα μου
        </h2>
      </div>

      <div style={{ marginBottom: '2rem', maxWidth: 400 }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            type="text"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            placeholder="Αναζήτηση με κωδικό έργου..."
            style={{ width: '100%', padding: '0.875rem 0.875rem 0.875rem 2.5rem', border: '1px solid #d0d0d0', fontSize: '1rem', fontFamily: 'inherit' }}
            data-testid="search-project-input"
          />
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="empty-state" data-testid="no-projects-message">
          <FolderOpen size={48} />
          <p>{searchCode ? 'Δεν βρέθηκαν έργα με αυτόν τον κωδικό' : 'Δεν υπάρχουν έργα ακόμα'}</p>
          <p>Δημιουργήστε ένα νέο έργο από την καρτέλα "Δημιουργία Έργου"</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredProjects.map((project) => (
            <div key={project.id} style={{ border: '1px solid #e0e0e0', background: '#fff' }} data-testid={`project-card-${project.project_code}`}>
              {/* Project Header - Clickable */}
              <div
                onClick={() => toggleProject(project)}
                style={{
                  padding: '1.25rem 1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  transition: 'background 0.2s',
                  background: expandedProject === project.id ? '#f9f9f9' : 'transparent'
                }}
                data-testid={`project-toggle-${project.project_code}`}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: 0 }}>
                  <span className="project-code">{project.project_code}</span>
                  <span style={{ fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.title}</span>
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>- {project.client_name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
                  <span
                    style={{
                      padding: '0.3rem 0.75rem',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      background: `${getStatusColor(project.status)}15`,
                      color: getStatusColor(project.status),
                      borderRadius: '3px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {project.status}
                  </span>
                  {expandedProject === project.id ? <ChevronUp size={20} color="#666" /> : <ChevronDown size={20} color="#666" />}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedProject === project.id && (
                <div style={{ borderTop: '1px solid #e0e0e0' }}>
                  {/* Sub-tabs */}
                  <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', background: '#fafafa' }}>
                    {[
                      { id: 'details', label: 'Λεπτομέρειες' },
                      { id: 'progress', label: 'Πρόοδος' },
                      { id: 'files', label: 'Αρχεία' },
                      { id: 'chat', label: 'Chat' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveSubTab(tab.id)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          border: 'none',
                          borderBottom: activeSubTab === tab.id ? '2px solid #4caf50' : '2px solid transparent',
                          background: 'transparent',
                          color: activeSubTab === tab.id ? '#4caf50' : '#666',
                          fontWeight: activeSubTab === tab.id ? 600 : 400,
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                        data-testid={`subtab-${tab.id}-${project.project_code}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {/* Details Tab */}
                    {activeSubTab === 'details' && (
                      <div data-testid={`details-tab-${project.project_code}`}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: 600 }}>
                          <div>
                            <span style={{ fontSize: '0.8rem', color: '#999', textTransform: 'uppercase' }}>Πελάτης</span>
                            <p style={{ margin: '0.25rem 0 0', fontWeight: 500, color: '#1a1a1a' }}>{project.client_name}</p>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.8rem', color: '#999', textTransform: 'uppercase' }}>Κωδικός</span>
                            <p style={{ margin: '0.25rem 0 0', fontWeight: 500, color: '#1a1a1a' }}>{project.project_code}</p>
                          </div>
                          {project.address && (
                            <div>
                              <span style={{ fontSize: '0.8rem', color: '#999', textTransform: 'uppercase' }}>Διεύθυνση</span>
                              <p style={{ margin: '0.25rem 0 0', color: '#333' }}>{project.address}</p>
                            </div>
                          )}
                          {project.systems && (
                            <div>
                              <span style={{ fontSize: '0.8rem', color: '#999', textTransform: 'uppercase' }}>Συστήματα</span>
                              <p style={{ margin: '0.25rem 0 0', color: '#333' }}>{project.systems}</p>
                            </div>
                          )}
                          {project.color && (
                            <div>
                              <span style={{ fontSize: '0.8rem', color: '#999', textTransform: 'uppercase' }}>Χρώμα</span>
                              <p style={{ margin: '0.25rem 0 0', color: '#333' }}>{project.color}</p>
                            </div>
                          )}
                          {project.description && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <span style={{ fontSize: '0.8rem', color: '#999', textTransform: 'uppercase' }}>Περιγραφή</span>
                              <p style={{ margin: '0.25rem 0 0', color: '#333', lineHeight: 1.5 }}>{project.description}</p>
                            </div>
                          )}
                        </div>
                        <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#999' }}>
                          Δημιουργήθηκε: {new Date(project.created_at).toLocaleDateString('el-GR')}
                        </div>
                      </div>
                    )}

                    {/* Progress Tab */}
                    {activeSubTab === 'progress' && (
                      <div data-testid={`progress-tab-${project.project_code}`}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '1.5rem', color: '#1a1a1a' }}>Πρόοδος Έργου</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {STAGES.map((stage, idx) => {
                            const currentIdx = getCurrentStageIndex(project.status);
                            const completed = isStageCompleted(project.id, stage);
                            const isCurrent = stage === project.status;
                            return (
                              <div
                                key={stage}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  padding: '0.75rem 1rem',
                                  background: isCurrent ? '#e8f5e9' : completed ? '#f1f8e9' : '#fafafa',
                                  border: isCurrent ? '1px solid #4caf50' : '1px solid #e0e0e0',
                                  borderRadius: '4px'
                                }}
                              >
                                {completed ? (
                                  <CheckCircle2 size={20} color="#4caf50" />
                                ) : isCurrent ? (
                                  <Clock size={20} color="#FF9800" />
                                ) : (
                                  <Circle size={20} color="#ccc" />
                                )}
                                <span style={{
                                  fontSize: '0.95rem',
                                  color: completed ? '#2e7d32' : isCurrent ? '#e65100' : '#999',
                                  fontWeight: isCurrent ? 600 : 400
                                }}>
                                  {stage}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Files Tab */}
                    {activeSubTab === 'files' && (
                      <div data-testid={`files-tab-${project.project_code}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                          <h3 style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, color: '#1a1a1a' }}>Αρχεία Έργου</h3>
                          <label className="btn-primary" style={{ cursor: 'pointer', opacity: uploadingFile ? 0.5 : 1 }}>
                            <Upload size={16} />
                            {uploadingFile ? 'Ανέβασμα...' : 'Ανέβασμα Αρχείου'}
                            <input
                              type="file"
                              onChange={(e) => handleFileUpload(project.id, e)}
                              style={{ display: 'none' }}
                              disabled={uploadingFile}
                              data-testid={`file-upload-${project.project_code}`}
                            />
                          </label>
                        </div>

                        {(projectFiles[project.id] || []).length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                            <FileText size={36} style={{ opacity: 0.4, marginBottom: '0.5rem' }} />
                            <p>Δεν υπάρχουν αρχεία ακόμα</p>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {(projectFiles[project.id] || []).map(file => (
                              <div key={file.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.75rem 1rem', border: '1px solid #e0e0e0', background: '#fafafa'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                  <FileText size={18} color="#667eea" />
                                  <div>
                                    <p style={{ margin: 0, fontWeight: 500, fontSize: '0.9rem' }}>{file.filename}</p>
                                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#999' }}>
                                      {(file.file_size / 1024).toFixed(1)} KB - {new Date(file.uploaded_at).toLocaleDateString('el-GR')}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeleteFile(project.id, file.id)}
                                  className="btn-icon danger"
                                  title="Διαγραφή"
                                  data-testid={`delete-file-${file.id}`}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Chat Tab */}
                    {activeSubTab === 'chat' && (
                      <div data-testid={`chat-tab-${project.project_code}`}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '1rem', color: '#1a1a1a' }}>
                          <MessageSquare size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                          Chat - {project.project_code}
                        </h3>

                        <div style={{
                          height: 300, overflowY: 'auto', border: '1px solid #e0e0e0',
                          padding: '1rem', marginBottom: '1rem', background: '#fafafa'
                        }}>
                          {(projectMessages[project.id] || []).length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#999' }}>
                              <p>Δεν υπάρχουν μηνύματα ακόμα</p>
                            </div>
                          ) : (
                            (projectMessages[project.id] || []).map(msg => {
                              const user = JSON.parse(localStorage.getItem('user') || '{}');
                              const isOwn = msg.sender_id === user.id;
                              return (
                                <div key={msg.id} style={{
                                  display: 'flex', flexDirection: 'column',
                                  alignItems: isOwn ? 'flex-end' : 'flex-start',
                                  marginBottom: '0.75rem'
                                }}>
                                  <span style={{ fontSize: '0.75rem', color: '#999', marginBottom: 2 }}>
                                    {msg.sender_name} ({msg.sender_role === 'admin' ? 'Admin' : 'Μηχανικός'})
                                  </span>
                                  <div style={{
                                    background: isOwn ? '#4caf50' : '#fff',
                                    color: isOwn ? '#fff' : '#333',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '12px',
                                    maxWidth: '70%',
                                    border: isOwn ? 'none' : '1px solid #e0e0e0',
                                    fontSize: '0.9rem',
                                    lineHeight: 1.4
                                  }}>
                                    {msg.content}
                                  </div>
                                  <span style={{ fontSize: '0.7rem', color: '#bbb', marginTop: 2 }}>
                                    {new Date(msg.created_at).toLocaleString('el-GR')}
                                  </span>
                                </div>
                              );
                            })
                          )}
                          <div ref={messagesEndRef} />
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(project.id)}
                            placeholder="Γράψτε μήνυμα..."
                            style={{
                              flex: 1, padding: '0.75rem', border: '1px solid #d0d0d0',
                              fontSize: '0.95rem', fontFamily: 'inherit'
                            }}
                            data-testid={`chat-input-${project.project_code}`}
                          />
                          <button
                            onClick={() => handleSendMessage(project.id)}
                            className="btn-primary"
                            data-testid={`chat-send-${project.project_code}`}
                          >
                            <Send size={18} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyProjects;
